import { ref, computed } from 'vue';
import { useBookmarkStore } from '~/stores/bookmarkStore';
import { useWebsiteConfigurationStore } from '~/stores/websiteConfigurationStore';

/**
 * Shared heart-toggle behaviour for a property announce (hero image, search
 * result cards, ...): live bookmark state from the Firestore mirror, an
 * optimistic override so the heart flips on click without waiting for the
 * API round-trip, and the auth gate for signed-out visitors.
 *
 * `getIds` returns `{ announceId, subjectId }` for the rendered listing —
 * either id is enough to resolve the bookmark; both are sent when available.
 * `getTrackingParams` (optional) returns extra GA4 event params for the
 * listing (item_name, city, price, currency — same shape as view_item).
 *
 * Usage:
 *   const { isBookmarked, bookmarkBusy, toggleBookmark } =
 *     usePropertyBookmark(() => ({
 *       announceId: listing.announce_id,
 *       subjectId: listing.property?.id,
 *     }));
 */
export function usePropertyBookmark(getIds, getTrackingParams = () => ({})) {
  const nuxtApp = useNuxtApp();
  const { $helpers } = nuxtApp;
  const bookmarkStore = useBookmarkStore();
  const { track } = useAnalytics();

  // no-op during SSR; starts the users/{id}/bookmarks watch on the client
  bookmarkStore.init();

  const bookmarkBusy = ref(false);
  const optimisticBookmarked = ref(null);

  const currentBookmark = computed(() => {
    const { announceId, subjectId } = getIds() || {};
    if (!announceId && !subjectId) return null;
    return bookmarkStore.find({
      announceId,
      subjectType: 'properties',
      subjectId,
    });
  });

  const isBookmarked = computed(
    () => optimisticBookmarked.value ?? !!currentBookmark.value
  );

  async function toggleBookmark() {
    if ($helpers.isOffline()) return;
    if (!nuxtApp.$currentUser()) {
      $helpers.openDialog('Auth');
      return;
    }
    if (bookmarkBusy.value) return;
    const { announceId, subjectId } = getIds() || {};
    if (!announceId && !subjectId) return;
    bookmarkBusy.value = true;
    try {
      if (currentBookmark.value) {
        optimisticBookmarked.value = false;
        track('remove_from_wishlist', {
          item_id: announceId ?? subjectId,
          ...getTrackingParams(),
        });
        await bookmarkStore.remove(currentBookmark.value.id);
      } else {
        // subject_type + subject_id are the only required fields upstream
        if (!subjectId) return;
        optimisticBookmarked.value = true;
        // GA4 recommended event name so wishlist adds surface in standard
        // reports and can seed remarketing audiences.
        track('add_to_wishlist', {
          item_id: announceId ?? subjectId,
          ...getTrackingParams(),
        });
        const configuration = useWebsiteConfigurationStore().configuration;
        await bookmarkStore.add({
          subject_type: 'properties',
          subject_id: String(subjectId),
          ...(announceId
            ? {
                announce_type: 'property-announces',
                announce_id: String(announceId),
              }
            : {}),
          website_id: String(configuration.websiteId),
          marketplace_id: String(configuration.marketplaceId),
          listing_feed_id: String(configuration.listingId),
        });
      }
    } catch (error) {
      console.error('[bookmarks] toggle failed', error);
    } finally {
      optimisticBookmarked.value = null;
      bookmarkBusy.value = false;
    }
  }

  return { isBookmarked, bookmarkBusy, toggleBookmark };
}
